// src/pages/TicketDeliveryManagement/modals/CreateTicketDeliveryModal.tsx
// Form lập phiếu giao vé: chọn Kế hoạch phát hành (mục 12, REQUIRE_FIXBUGS.md — chuyển vào trong
// modal thay vì chọn sẵn ngoài trang) + Đại lý (autocomplete gọi API `/system-api/Agent` có
// debounce) + nhiều dòng Symbol/dải số (thêm/xoá dòng động, Symbol chỉ chọn được trong các ký
// hiệu CÒN TỒN của kế hoạch đang chọn). Validate client-side cơ bản (mục 4.8) — validate thật
// (tồn kho, chồng lấn dải) vẫn ở backend (4.5).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import QrScannerModal from 'pages/TicketLookup/components/QrScannerModal';
import { decodeScannedTicket } from 'helpers/ticketQrDecode';
import {
  getAgents,
  getAvailableDeliveryRangesApi,
  getSystemConfig,
  checkDeliveryRangeOverlapApi,
  getAgentByIdApi,
  decodeTicketToken,
} from 'helpers/fakebackend_helper';
import { CacheService } from 'services/cache';
import { CacheConfig } from 'services/cache/CacheConfig';
import { IAgent } from 'slices/agentManagement/types';
import { IssuancePlan } from 'pages/IssuancePlan/types';
import { SystemConfiguration } from 'pages/SystemConfiguration/types';
import { fetchTicketStockBalances } from 'slices/ticketDelivery/thunk';
import { clearStockBalances } from 'slices/ticketDelivery/reducer';
import {
  CreateTicketDeliveryPayload,
  DeliveryLineInput,
  TicketStockBalance,
  calcLineQuantity,
  validateDeliveryLine,
} from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_delivery_management');

export interface AgentOption {
  value: number;
  label: string;
  code?: string | null;
  name?: string | null;
}

interface SymbolOption {
  value: string;
  label: string;
}

// Mục 3, README.md (Cập nhật) — 1 dải số CÒN TRỐNG (chưa giao cho đại lý nào) của ký hiệu đang
// chọn, dùng để auto-fill thay vì fill nguyên dải số của cả kế hoạch (mục 11 cũ).
interface AvailableRange {
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
  quantity: number;
}

interface CreateTicketDeliveryModalProps {
  isOpen: boolean;
  submitting: boolean;
  // Danh sách kế hoạch phát hành đã Accept (đã nhập kho) để chọn ngay trong modal.
  plans: IssuancePlan[];
  onClose: () => void;
  onSubmit: (payload: CreateTicketDeliveryPayload, agent: AgentOption | null) => void;
}

const emptyLine = (): DeliveryLineInput => ({ symbol: '', rangeStart: '', rangeEnd: '' });

// Dropdown Symbol nằm trong bảng bên trong Modal — portal ra body + z-index cao để menu không bị
// modal cắt mất, và hạ chiều cao control cho khớp các ô Input size sm cùng dòng.
// `base: any`: type `StylesConfig` của react-select 5.10 không resolve được dưới TS/CRA hiện tại
// (TS2709), nên chỉ chú thích tối thiểu — giống cách IssuancePlan đang dùng react-select.
const symbolSelectStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  control: (base: any) => ({ ...base, minHeight: 31 }),
};

const formatNumber = (value: number) => value.toLocaleString('vi-VN');

const getPayloadItems = (response: any): any[] => {
  const payload = response?.data || response;
  return payload?.items || payload?.data || payload || [];
};

const CreateTicketDeliveryModal: React.FC<CreateTicketDeliveryModalProps> = ({
  isOpen,
  submitting,
  plans,
  onClose,
  onSubmit,
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { stockBalances } = useSelector((state: any) => state.TicketDelivery) as {
    stockBalances: TicketStockBalance[];
  };
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null);
  const [agentSearchInput, setAgentSearchInput] = useState('');
  // 20 đại lý đang hoạt động đầu tiên — hiện sẵn khi mở dropdown mà chưa gõ gì
  const [defaultAgentOptions, setDefaultAgentOptions] = useState<AgentOption[]>([]);
  const [note, setNote] = useState('');
  // Mục 8.g, README_UPDATE.md — giá vé áp dụng cho phiếu, prefill từ System Configuration lúc mở
  // modal, cho sửa tay trước khi lập phiếu.
  const [unitPrice, setUnitPrice] = useState('');
  const [lines, setLines] = useState<DeliveryLineInput[]>([emptyLine()]);
  const [touched, setTouched] = useState(false);
  // Mục 4 (README.md §7) — cảnh báo trùng dải số theo từng dòng (check tăng dần khi nhập), thay cho popup.
  const [overlapWarnings, setOverlapWarnings] = useState<Record<number, string | null>>({});
  const agentNameCacheRef = useRef<Record<number, string>>({});
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const agentSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const selectedPlan = useMemo(
    () => plans.find((plan) => String(plan.id) === selectedPlanId) || null,
    [plans, selectedPlanId],
  );

  // Reset form mỗi lần mở lại modal
  useEffect(() => {
    if (isOpen) {
      setSelectedPlanId('');
      setSelectedAgent(null);
      setAgentSearchInput('');
      setNote('');
      setUnitPrice('');
      setLines([emptyLine()]);
      setTouched(false);
      setQrModalOpen(false);
      setOverlapWarnings({});
    }
  }, [isOpen]);

  // Mục 8.g, README_UPDATE.md — prefill giá vé từ System Configuration (cache-first, giống
  // helpers/systemConfigCache.ts — không dispatch vào Redux vì modal này chỉ cần đọc 1 lần).
  useEffect(() => {
    if (!isOpen) return;

    const cached = CacheService.get<SystemConfiguration>(CacheConfig.SYSTEM_CONFIG.key);
    if (cached.cached && cached.data) {
      setUnitPrice(String(cached.data.defaultTicketPrice ?? ''));
      return;
    }

    let cancelled = false;
    getSystemConfig()
      .then((response: any) => {
        if (cancelled) return;
        const config = response?.data || response;
        if (config?.defaultTicketPrice !== undefined && config?.defaultTicketPrice !== null) {
          setUnitPrice(String(config.defaultTicketPrice));
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Quét QR vé bằng camera — GIẢI MÃ token in trên vé (URL đầy đủ hoặc token trần) thành
  // {kỳ phát hành, số vé, ký hiệu} rồi điền vào 1 dòng dải giao. Vé đơn: dải chỉ gồm đúng 1 vé nên
  // "Số bắt đầu" = "Số kết thúc" = số vé vừa quét. Đối chiếu kỳ phát hành + ký hiệu với tồn kho đang
  // chọn trước khi điền để tránh nhầm.
  const handleQrScanned = async (decodedText: string) => {
    setQrModalOpen(false);
    const raw = decodedText.trim();
    if (!raw) return;

    const decoded = await decodeScannedTicket(raw, decodeTicketToken);
    if (!decoded) {
      toast.error(st('khong_doc_duoc_ma_qr_nay_message'));
      return;
    }

    const wasPlanEmpty = !selectedPlanId;

    if (wasPlanEmpty) {
      // Vé ĐẦU TIÊN — chưa chọn kế hoạch: tự set kế hoạch phát hành theo KỲ của vé vừa quét.
      const matchedPlan = plans.find((plan) => String(plan.id) === String(decoded.lotteryPeriodId));
      if (!matchedPlan) {
        toast.error(
          st('ky_phat_hanh_cua_ve_vua_message') +
            st('chua_nhap_kho_hoac_da_dong_message'),
        );
        return;
      }
      setSelectedPlanId(String(decoded.lotteryPeriodId));
    } else if (decoded.lotteryPeriodId !== Number(selectedPlanId)) {
      toast.error(st('ve_vua_quet_khong_thuoc_ke_message'));
      return;
    }

    // Vừa set kế hoạch thì tồn kho (symbolOptions) chưa tải kịp -> bỏ qua kiểm tra ký hiệu tại đây,
    // để bước validate lúc lưu phiếu lo. Kế hoạch đã có tồn kho mới chặn ký hiệu ngoài danh sách.
    if (!wasPlanEmpty && symbolOptions.length > 0 && !symbolOptions.some((option) => option.value === decoded.symbol)) {
      toast.error(
        st('ky_hieu_symbol_cua_ve_vua_message', { symbol: decoded.symbol }),
      );
      return;
    }

    // Đặt KỲ xong -> đặt KÝ HIỆU + số vé (vé đơn: bắt đầu = kết thúc = số vé).
    setLines((prev) => {
      const newLine: DeliveryLineInput = {
        symbol: decoded.symbol,
        rangeStart: decoded.ticketNumber,
        rangeEnd: decoded.ticketNumber,
      };
      // Nếu dòng cuối còn trống hoàn toàn thì điền vào đó, ngược lại thêm dòng mới.
      const last = prev[prev.length - 1];
      const lastEmpty = last && !last.symbol && !last.rangeStart && !last.rangeEnd;
      return lastEmpty ? [...prev.slice(0, -1), newLine] : [...prev, newLine];
    });
    toast.success(
      wasPlanEmpty
        ? st('da_chon_ke_hoach_theo_ky_message', { symbol: decoded.symbol, ticket_number: decoded.ticketNumber })
        : st('da_dien_ve_symbol_ticket_number_message', { symbol: decoded.symbol, ticket_number: decoded.ticketNumber }),
    );
  };

  // Tải tồn kho tổng theo Symbol mỗi khi đổi kế hoạch đang chọn trong modal (mục 12) — trước đây
  // do trang cha tải khi chọn kế hoạch ngoài trang, nay chuyển hẳn vào đây vì việc chọn kế hoạch
  // chỉ còn xảy ra trong modal này.
  useEffect(() => {
    if (!isOpen) return;
    if (!selectedPlanId) {
      dispatch(clearStockBalances());
      return;
    }
    dispatch(fetchTicketStockBalances({ planId: Number(selectedPlanId) }) as any);
  }, [dispatch, isOpen, selectedPlanId]);

  const mapAgentOption = (agent: IAgent): AgentOption => ({
    value: Number(agent.id),
    label: `${agent.code} - ${agent.name}`,
    code: agent.code,
    name: agent.name,
  });

  // Autocomplete đại lý: gọi API theo từ khoá (BE hỗ trợ `Search` theo Mã hoặc Tên đại lý,
  // xem GetPaginatedAgentsQuery), debounce 350ms để không gọi API trên từng ký tự gõ.
  // Không nạp sẵn toàn bộ danh sách vì số lượng đại lý có thể rất lớn.
  const loadAgentOptions = useCallback(
    (inputValue: string): Promise<AgentOption[]> =>
      new Promise((resolve) => {
        if (agentSearchTimer.current) clearTimeout(agentSearchTimer.current);
        agentSearchTimer.current = setTimeout(async () => {
          try {
            const response = await getAgents({
              PageIndex: 1,
              // BE giới hạn PageSize tối đa 100, vượt sẽ bị ép về 20 — giữ 20 cho gọn dropdown
              PageSize: 20,
              Status: 'Active',
              Search: inputValue || undefined,
            });
            resolve((getPayloadItems(response) as IAgent[]).map(mapAgentOption));
          } catch (error: any) {
            toast.error(error?.message || st('khong_the_tim_dai_ly_message'));
            resolve([]);
          }
        }, 350);
      }),
    [],
  );

  // Nạp danh sách gợi ý mặc định mỗi lần mở modal (để dropdown không trống lúc chưa gõ)
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const loadDefaults = async () => {
      try {
        const response = await getAgents({ PageIndex: 1, PageSize: 20, Status: 'Active' });
        if (!cancelled) {
          setDefaultAgentOptions((getPayloadItems(response) as IAgent[]).map(mapAgentOption));
        }
      } catch {
        if (!cancelled) setDefaultAgentOptions([]);
      }
    };

    loadDefaults();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Chỉ gợi ý những ký hiệu CÒN TỒN (> 0) của kế hoạch đang chọn — tái dùng dữ liệu tồn kho đã
  // fetch cho bảng tồn kho tổng, không gọi thêm API. Dùng Select (không phải Creatable) nên người
  // dùng chỉ gõ để lọc, không tạo được symbol tự do ngoài danh sách.
  const symbolOptions = useMemo<SymbolOption[]>(
    () =>
      stockBalances
        .filter((balance) => (balance.quantity || 0) > 0)
        .map((balance) => ({
          value: balance.symbol,
          label: st('symbol_con_value_ve_label', { symbol: balance.symbol, value: formatNumber(balance.quantity || 0) }),
        })),
    [stockBalances],
  );

  const lineErrors = useMemo(() => lines.map((line) => validateDeliveryLine(line)), [lines]);
  const totalQuantity = useMemo(
    () => lines.reduce((sum, line) => sum + calcLineQuantity(line), 0),
    [lines],
  );

  const planError = !selectedPlanId ? st('chua_chon_ke_hoach_phat_hanh_text') : null;
  const agentError = !selectedAgent ? st('chua_chon_dai_ly_nhan_ve_text') : null;
  const hasOverlap = Object.values(overlapWarnings).some(Boolean);
  const hasError =
    !!planError || !!agentError || lineErrors.some((error) => error !== null) || hasOverlap;

  // Mục 4 (README.md §7) — tên đại lý resolve qua system-service (ticket-service không có dữ liệu Agent).
  const resolveAgentName = useCallback(async (agentId?: number | null): Promise<string> => {
    if (!agentId) return st('dai_ly_khac_text');
    if (agentNameCacheRef.current[agentId]) return agentNameCacheRef.current[agentId];
    try {
      const res: any = await getAgentByIdApi(agentId);
      const data = res?.data || res;
      const name = data?.name
        ? st('dai_ly_value_name_text', { value: data.code ? data.code + ' - ' : '', name: data.name })
        : st('dai_ly_agent_id_text', { agent_id: agentId });
      agentNameCacheRef.current[agentId] = name;
      return name;
    } catch {
      return st('dai_ly_agent_id_text', { agent_id: agentId });
    }
  }, []);

  // Trả về câu cảnh báo tiếng Việt nếu dòng dải số này trùng phiếu khác, ngược lại null.
  const computeRowOverlap = useCallback(
    async (line: DeliveryLineInput): Promise<string | null> => {
      const symbol = line.symbol.trim();
      const rangeStart = line.rangeStart.trim();
      const rangeEnd = line.rangeEnd.trim();
      if (!selectedPlanId || !symbol || !rangeStart || !rangeEnd) return null;
      try {
        const res: any = await checkDeliveryRangeOverlapApi({
          planId: Number(selectedPlanId),
          symbol,
          rangeStart,
          rangeEnd,
        });
        const items: any[] = res?.data || res || [];
        if (!Array.isArray(items) || items.length === 0) return null;
        const first = items[0];
        const agentName = await resolveAgentName(first.agentId);
        let msg =
          st('dai_ve_range_start_range_end_text', { range_start: rangeStart, range_end: rangeEnd, symbol: symbol, agent_name: agentName }) +
          st('tai_phieu_delivery_code_text', { delivery_code: first.deliveryCode });
        if (items.length > 1) msg += st('va_value_phieu_khac_text', { value: items.length - 1 });
        return msg + '.';
      } catch {
        return null;
      }
    },
    [selectedPlanId, resolveAgentName],
  );

  // Check trùng TĂNG DẦN: mỗi khi người dùng sửa dải số / ký hiệu / đổi kế hoạch thì kiểm lại toàn bộ
  // các dòng (debounce 500ms), dựng lại map cảnh báo từ đầu để không còn key cũ sau khi xoá dòng.
  useEffect(() => {
    if (!selectedPlanId) {
      setOverlapWarnings({});
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const results = await Promise.all(lines.map((line) => computeRowOverlap(line)));
      if (cancelled) return;
      const map: Record<number, string | null> = {};
      results.forEach((r, i) => {
        map[i] = r;
      });
      setOverlapWarnings(map);
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [lines, selectedPlanId, computeRowOverlap]);

  const handleLineChange = (index: number, field: keyof DeliveryLineInput, value: string) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
  };

  const handleAddLine = () => {
    setLines((prev) => [...prev, emptyLine()]);
  };

  const handleRemoveLine = (index: number) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);

    // Không hiện toast góc phải khi nhập sai (mục 11) — Alert đỏ dưới bảng dải số + dòng lỗi đỏ
    // dưới ô chọn kế hoạch/đại lý (inline ngay dưới từng ô) là đủ, tránh trùng lặp thông báo.
    if (hasError || !selectedAgent) {
      return;
    }

    onSubmit(
      {
        issuancePlanId: Number(selectedPlanId),
        agentId: selectedAgent.value,
        note: note.trim() || undefined,
        unitPrice: unitPrice.trim() !== '' ? Number(unitPrice) : undefined,
        lines: lines.map((line) => ({
          symbol: line.symbol.trim(),
          rangeStart: line.rangeStart.trim(),
          rangeEnd: line.rangeEnd.trim(),
        })),
      },
      selectedAgent,
    );
  };

  const canEnableAgent = PageActionPermission.canEnable(location.pathname, 'create-input-agent');
  const canEnableSymbol = PageActionPermission.canEnable(
    location.pathname,
    'create-input-line-symbol',
  );

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>{st('lap_phieu_giao_ve_cho_dai_title')}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Alert color="info" className="py-2">
            <i className="ri-information-line me-1"></i>
            {st('he_thong_se_kiem_tra_ton_message')}
          </Alert>

          <Row className="g-3">
            <Col md={6}>
              <FormGroup>
                <Label for="delivery-plan">
                  {st('ke_hoach_phat_hanh_da_nhap_label')} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="delivery-plan"
                  className="form-select"
                  value={selectedPlanId}
                  data-action-code="create-input-plan"
                  data-action-name="Chọn kế hoạch phát hành"
                  disabled={
                    !PageActionPermission.canEnable(location.pathname, 'create-input-plan')
                  }
                  onChange={(event) => setSelectedPlanId(event.target.value)}
                >
                  <option value="">{st('chon_ke_hoach_phat_hanh_option')}</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.planCode} — {plan.ticketTypeName || plan.ticketType}
                    </option>
                  ))}
                </Input>
                {touched && planError && <div className="text-danger small mt-1">{planError}</div>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="delivery-agent">
                  {st('dai_ly_nhan_ve_label')} <span className="text-danger">*</span>
                </Label>
                {/* Bọc div để scanner nhận diện đúng đây là 1 Input (react-select không render
                    thẳng thẻ <input> mang được data-action-code) — RULEPERMISSION Rule 1 */}
                <div data-action-code="create-input-agent" data-action-name="Chọn đại lý nhận vé">
                  <AsyncSelect
                    inputId="delivery-agent"
                    isDisabled={!canEnableAgent}
                    isClearable
                    cacheOptions
                    defaultOptions={defaultAgentOptions}
                    loadOptions={loadAgentOptions}
                    value={selectedAgent}
                    inputValue={agentSearchInput}
                    onInputChange={(value, actionMeta) => {
                      if (actionMeta.action === 'input-change') setAgentSearchInput(value);
                      return value;
                    }}
                    onChange={(option) => {
                      setSelectedAgent(option as AgentOption | null);
                      // Xoá text tìm kiếm để react-select hiển thị ngay giá trị vừa chọn
                      setAgentSearchInput('');
                    }}
                    placeholder={st('go_ma_hoac_ten_dai_ly_placeholder')}
                    noOptionsMessage={() => st('khong_tim_thay_dai_ly_phu_text')}
                    loadingMessage={() => st('dang_tim_text')}
                  />
                </div>
                {touched && agentError && <div className="text-danger small mt-1">{agentError}</div>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="delivery-note">{st('ghi_chu_label')}</Label>
                <Input
                  type="text"
                  id="delivery-note"
                  placeholder={st('ghi_chu_phieu_giao_khong_bat_placeholder')}
                  maxLength={500}
                  value={note}
                  data-action-code="create-input-note"
                  data-action-name="Ghi chú phiếu giao vé"
                  disabled={!PageActionPermission.canEnable(location.pathname, 'create-input-note')}
                  onChange={(event) => setNote(event.target.value)}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="delivery-unit-price">{st('gia_ve_d_ve_label')}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  id="delivery-unit-price"
                  placeholder={st('gia_ve_mac_dinh_dien_san_placeholder')}
                  value={unitPrice === '' ? '' : Number(unitPrice).toLocaleString('vi-VN')}
                  data-action-code="create-input-unit-price"
                  data-action-name="Giá vé áp dụng cho phiếu giao"
                  disabled={
                    !PageActionPermission.canEnable(location.pathname, 'create-input-unit-price')
                  }
                  onChange={(event) => setUnitPrice(event.target.value.replace(/\D/g, ''))}
                />
              </FormGroup>
            </Col>
          </Row>

          <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
            <h6 className="mb-0">{st('chi_tiet_dai_so_giao_title')}</h6>
            <div className="d-flex gap-2">
              {PageActionPermission.canDisplay(location.pathname, 'button-scan-qr') && (
                <Button
                  color="success"
                  outline
                  size="sm"
                  type="button"
                  data-action-code="button-scan-qr"
                  data-action-name="Quét QR vé"
                  onClick={() => setQrModalOpen(true)}
                >
                  <i className="ri-qr-scan-2-line align-bottom me-1"></i>
                  {st('quet_qr_button')}
                </Button>
              )}
              {PageActionPermission.canDisplay(location.pathname, 'button-add-line') && (
                <Button
                  color="primary"
                  outline
                  size="sm"
                  type="button"
                  data-action-code="button-add-line"
                  data-action-name="Thêm dòng dải số"
                  onClick={handleAddLine}
                >
                  <i className="ri-add-line align-bottom me-1"></i>
                  {st('them_dong_button')}
                </Button>
              )}
            </div>
          </div>

          {!selectedPlanId ? (
            <Alert color="secondary" className="py-2">
              <i className="ri-information-line me-1"></i>
              {st('vui_long_chon_ke_hoach_phat_message')}
            </Alert>
          ) : (
            symbolOptions.length === 0 && (
              <Alert color="warning" className="py-2">
                <i className="ri-alert-line me-1"></i>
                {st('ke_hoach_nay_khong_con_ky_message')}
              </Alert>
            )
          )}

          <div className="table-responsive">
            <table className="table table-bordered table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 220 }}>{st('ky_hieu_con_ton_column')}</th>
                  <th>{st('so_bat_dau_column')}</th>
                  <th>{st('so_ket_thuc_column')}</th>
                  <th className="text-end" style={{ width: 110 }}>
                    {st('so_luong_column')}
                  </th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <React.Fragment key={index}>
                  <tr>
                    <td>
                      <div
                        data-action-code="create-input-line-symbol"
                        data-action-name="Ký hiệu dải số giao"
                      >
                        <Select
                          isDisabled={!canEnableSymbol}
                          isClearable
                          options={symbolOptions}
                          value={
                            symbolOptions.find((option) => option.value === line.symbol) || null
                          }
                          onChange={(option: SymbolOption | null) => {
                            // Chọn ký hiệu -> auto-fill Số bắt đầu/Số kết thúc theo dải số CÒN
                            // TRỐNG thực sự của ký hiệu này trong kế hoạch (mục 3, README.md Cập
                            // nhật — trước đây fill nguyên dải số của cả kế hoạch, không loại trừ
                            // phần đã giao cho đại lý khác). Người dùng vẫn sửa được nếu chỉ muốn
                            // giao 1 phần dải. Bỏ chọn (Clear) thì xoá luôn 2 ô này.
                            const symbol = option?.value || '';
                            setLines((prev) =>
                              prev.map((line, i) =>
                                i === index ? { ...line, symbol, rangeStart: '', rangeEnd: '' } : line,
                              ),
                            );

                            if (!option || !selectedPlanId) return;

                            getAvailableDeliveryRangesApi({
                              planId: Number(selectedPlanId),
                              symbol: option.value,
                            })
                              .then((response: any) => {
                                const payload = response?.data || response;
                                const ranges = (payload?.items || payload?.data || payload || []) as AvailableRange[];
                                const first = ranges[0];

                                if (!first) {
                                  toast.warning(
                                    st('ky_hieu_value_hien_khong_con_message', { value: option.value }),
                                  );
                                  return;
                                }
                                if (ranges.length > 1) {
                                  toast.info(
                                    st('ky_hieu_value_co_length_dai_message', { value: option.value, length: ranges.length }),
                                  );
                                }

                                setLines((prev) =>
                                  prev.map((l, i) =>
                                    i === index && l.symbol === option.value
                                      ? { ...l, rangeStart: first.rangeStart, rangeEnd: first.rangeEnd }
                                      : l,
                                  ),
                                );
                              })
                              .catch(() => {
                                // Fallback: API lỗi thì giữ hành vi cũ (fill nguyên dải số của kế
                                // hoạch) thay vì để trống, người dùng vẫn tự chỉnh lại được.
                                setLines((prev) =>
                                  prev.map((l, i) =>
                                    i === index && l.symbol === option.value
                                      ? {
                                          ...l,
                                          rangeStart: selectedPlan?.numberRangeStart || '',
                                          rangeEnd: selectedPlan?.numberRangeEnd || '',
                                        }
                                      : l,
                                  ),
                                );
                              });
                          }}
                          placeholder={st('go_de_tim_ky_hieu_placeholder')}
                          noOptionsMessage={() => st('khong_co_ky_hieu_con_ton_text')}
                          menuPortalTarget={document.body}
                          styles={symbolSelectStyles}
                        />
                      </div>
                    </td>
                    <td>
                      <Input
                        type="text"
                        bsSize="sm"
                        placeholder={st('vd_000001_placeholder')}
                        value={line.rangeStart}
                        data-action-code="create-input-line-range-start"
                        data-action-name="Số bắt đầu dải giao"
                        disabled={
                          !PageActionPermission.canEnable(
                            location.pathname,
                            'create-input-line-range-start',
                          )
                        }
                        onChange={(event) =>
                          handleLineChange(index, 'rangeStart', event.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Input
                        type="text"
                        bsSize="sm"
                        placeholder={st('vd_001000_placeholder')}
                        value={line.rangeEnd}
                        data-action-code="create-input-line-range-end"
                        data-action-name="Số kết thúc dải giao"
                        disabled={
                          !PageActionPermission.canEnable(
                            location.pathname,
                            'create-input-line-range-end',
                          )
                        }
                        onChange={(event) => handleLineChange(index, 'rangeEnd', event.target.value)}
                      />
                    </td>
                    <td className="text-end fw-semibold">{formatNumber(calcLineQuantity(line))}</td>
                    <td className="text-center">
                      {lines.length > 1 &&
                        PageActionPermission.canDisplay(location.pathname, 'button-remove-line') && (
                          <Button
                            color="danger"
                            outline
                            size="sm"
                            className="btn-icon"
                            type="button"
                            title={st('xoa_dong_title')}
                            data-action-code="button-remove-line"
                            data-action-name="Xoá dòng dải số"
                            onClick={() => handleRemoveLine(index)}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        )}
                    </td>
                  </tr>
                  {overlapWarnings[index] && (
                    <tr>
                      <td colSpan={5} className="border-0 pt-0">
                        <div className="text-danger small">
                          <i className="ri-error-warning-line align-bottom me-1"></i>
                          {overlapWarnings[index]}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <th colSpan={3}>{st('tong_so_ve_giao_column')}</th>
                  <th className="text-end">{formatNumber(totalQuantity)}</th>
                  <th></th>
                </tr>
              </tfoot>
            </table>
          </div>

          {touched && lineErrors.some((error) => error !== null) && (
            <Alert color="danger" className="mt-3 mb-0 py-2">
              <ul className="mb-0 ps-3">
                {lineErrors.map((error, index) =>
                  error ? (
                    <li key={index}>
                      {st('dong_text')} {index + 1}: {error}
                    </li>
                  ) : null,
                )}
              </ul>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={onClose} disabled={submitting}>
            {st('huy_button')}
          </Button>
          {PageActionPermission.canDisplay(location.pathname, 'button-create-submit') && (
            <Button
              color="primary"
              type="submit"
              data-action-code="button-create-submit"
              data-action-name="Lưu phiếu giao vé"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {st('dang_luu_text')}
                </>
              ) : (
                st('luu_phieu_text')
              )}
            </Button>
          )}
        </ModalFooter>
      </Form>
      <QrScannerModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        onScanned={handleQrScanned}
      />
    </Modal>
  );
};

export default CreateTicketDeliveryModal;
